import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@/stores/RootStore";
import type { Listing, ListingsSearchParams } from "@/shared/types/listings";

/**
 * ListingsStore - State management for listings feature
 *
 * KEY PATTERN: API access via this.root.api.listings
 * - Never import API class directly
 * - Never instantiate API (done in RootStore)
 * - Access auth token via this.root.authStore
 */
export class ListingsStore {
  // =====================
  // Observable State
  // =====================
  listings: Listing[] = [];
  selectedListing: Listing | null = null;
  // Use Partial because we might not have all params (like locale) initially
  searchParams: Partial<ListingsSearchParams> = {};
  loading = false;
  error: string | null = null;
  totalCount = 0;
  hasMore = false;
  private _cursor?: string;

  constructor(private root: RootStore) {
    makeAutoObservable(this, {
      root: false,
      _cursor: false,
    } as any);
  }

  // =====================
  // Computed
  // =====================
  get isEmpty(): boolean {
    return this.listings.length === 0 && !this.loading;
  }

  get currentLocale(): "en" | "ja" | "ru" {
    return this.root.uiStore.locale;
  }

  // =====================
  // Actions
  // =====================

  /**
   * Search public listings
   * Uses API injected via RootStore
   */
  async search(params: Partial<ListingsSearchParams>): Promise<void> {
    this.loading = true;
    this.error = null;
    this.searchParams = params;

    try {
      // ✅ Access API through RootStore
      const response = await this.root.api.listings.searchListings({
        ...params,
        locale: this.currentLocale,
      });

      runInAction(() => {
        // Client-side safety filter to handle backend bugs/mock limitations
        // Force-filter results based on critical parameters
        let filteredListings = response.listings;

        if (params.rent_max) {
          filteredListings = filteredListings.filter(
            (l) => l.financials.rent.amount <= params.rent_max!,
          );
        }
        if (params.rent_min) {
          filteredListings = filteredListings.filter(
            (l) => l.financials.rent.amount >= params.rent_min!,
          );
        }
        if (params.size_min) {
          filteredListings = filteredListings.filter(
            (l) => l.property.area_sqm >= params.size_min!,
          );
        }
        if (params.size_max) {
          filteredListings = filteredListings.filter(
            (l) => l.property.area_sqm <= params.size_max!,
          );
        }

        this.listings = filteredListings;
        this.totalCount = response.total_count; // Note: Total count might be inaccurate if we filter client-side
        this.hasMore = response.has_more;
        this._cursor = response.next_cursor;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : "Search failed";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Fetch partner's own listings (authenticated)
   * Gets token from AuthStore via RootStore
   */
  async fetchMyListings(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      // ✅ Get auth token from AuthStore via root
      const token = await this.root.authStore.getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // ✅ Use API via root
      const response = await this.root.api.listings.fetchPartnerListings(token);

      runInAction(() => {
        this.listings = response.listings;
        this.totalCount = response.total_count;
        this.hasMore = response.has_more;
        this._cursor = response.next_cursor;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : "Failed to load listings";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Load single listing by ID
   */
  async loadListing(id: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const listing = await this.root.api.listings.getListing(id);
      runInAction(() => {
        this.selectedListing = listing;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : "Listing not found";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Load more listings (pagination)
   */
  async loadMore(): Promise<void> {
    if (!this.hasMore || !this._cursor || this.loading) return;

    this.loading = true;

    try {
      const response = await this.root.api.listings.loadMoreListings(
        {
          ...this.searchParams,
          locale: this.currentLocale,
        },
        this._cursor,
      );

      runInAction(() => {
        this.listings = [...this.listings, ...response.listings];
        this.totalCount = response.total_count;
        this.hasMore = response.has_more;
        this._cursor = response.next_cursor;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : "Failed to load more";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Update a listing (Partner)
   */
  async updateListing(
    id: string,
    data: any, // using any to avoid importing huge UpdateRequest type here, or import it
  ): Promise<void> {
    // Optimistic update or just wait for refresh?
    // For now, let's just call API and refresh to ensure consistency
    const token = await this.root.authStore.getIdToken();
    if (!token) throw new Error("Authentication required");

    await this.root.api.listings.updateListing(token, id, data);

    // Update local state to reflect change immediately (Optimistic-ish)
    runInAction(() => {
      if (this.selectedListing?.id === id) {
        this.selectedListing = { ...this.selectedListing, ...data };
      }
      this.listings = this.listings.map((l) =>
        l.id === id ? { ...l, ...data } : l,
      );
    });
  }

  /**
   * Delete a listing (Partner)
   */
  async deleteListing(id: string): Promise<void> {
    const token = await this.root.authStore.getIdToken();
    if (!token) throw new Error("Authentication required");

    await this.root.api.listings.deleteListing(token, id);

    // Update local state
    runInAction(() => {
      this.listings = this.listings.filter((l) => l.id !== id);
      if (this.selectedListing?.id === id) {
        this.selectedListing = null;
      }
    });
  }

  /**
   * Reset store state
   */
  reset(): void {
    this.listings = [];
    this.selectedListing = null;
    this.searchParams = {};
    this.loading = false;
    this.error = null;
    this.totalCount = 0;
    this.hasMore = false;
    this._cursor = undefined;
  }
}
